import React from 'react';
import { applyTypography, Chip } from '@repo/common';
import { ReactComponent as LocationIcon20 } from '@repo/common/assets/icons/20px/icon_20px_location.svg';
import { ReactComponent as BillsIcon20 } from '@repo/common/assets/icons/20px/icon_20px_bills.svg';
import DateSlotCard from '../PathFeed/DateSlotCard';
import {
  DEFAULT_WORKSHOP_CATEGORY,
  getWorkshopCategory,
} from '../utils/workshopCategory';
import { formatWorkshopPrice } from '../utils/priceUtils';

const WorkshopInfo = ({
  step = DEFAULT_WORKSHOP_CATEGORY.value,
  title = 'Workshop name',
  dateSlots,
  location = 'Suwon-si',
  price = '15.00$',
  // 숫자로 price 가 올 때 쓰는 통화 (KRW → "55,000won", USD → "15.00$")
  currency = 'USD',
  description = '',
  selectedDateSlot,
  onSelectDateSlot,
  middleContent = null,
  isBlocked = false,
  // 결제 방식 안내 한 줄 (계좌이체 워크숍). 비어 있으면 그리지 않는다
  paymentNote = '',
}) => {
  const displayDateSlots = Array.isArray(dateSlots) ? dateSlots : [];
  const categoryInfo = getWorkshopCategory(step) ?? DEFAULT_WORKSHOP_CATEGORY;
  const displayStep = categoryInfo.label;
  const displayCategory = categoryInfo.title;
  const getDisplayPrice = (rawPrice) => {
    if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
      return rawPrice;
    }

    if (typeof rawPrice === 'number') {
      return rawPrice <= 0 ? 'Free' : formatWorkshopPrice(rawPrice, currency);
    }

    const text = String(rawPrice).trim();
    if (!text) return text;
    if (text.toLowerCase() === 'free') return 'Free';

    const normalized = text
      .replace(/USD|usd|\$/g, '')
      .replace(/,/g, '')
      .trim();
    const directNum = Number.parseFloat(normalized);
    if (!Number.isNaN(directNum) && directNum <= 0) return 'Free';

    const numericMatch = normalized.match(/-?\d+(\.\d+)?/);
    if (numericMatch) {
      const parsed = Number.parseFloat(numericMatch[0]);
      if (!Number.isNaN(parsed) && parsed <= 0) return 'Free';
    }

    return text;
  };

  const displayPrice = getDisplayPrice(price);

  const handleSlotClick = (slotId, available = true) => {
    if (isBlocked) return;
    const slot = displayDateSlots.find(
      (item) => String(item?.id) === String(slotId),
    );
    // 이미 지난 날짜/시간 슬롯은 선택 불가
    if (slot?.isPast) return;
    const isFull = String(slot?.status || '')
      .toLowerCase()
      .includes('full');
    const workshopStatus = String(slot?.workshopStatus || '').toLowerCase();
    const isStatusSelectable =
      workshopStatus === 'inbag' ||
      workshopStatus === 'onwaitlist' ||
      workshopStatus === 'booked' ||
      workshopStatus === 'pendingpayment';
    if (available === false && !isFull && !isStatusSelectable) return;
    onSelectDateSlot(selectedDateSlot === slotId ? null : slotId);
  };

  return (
    <div className='flex flex-col w-full'>
      {/* Header */}
      <div className='order-1 flex flex-col gap-[4px] items-start pb-[12px] pt-0 px-0 relative w-full tabletH:gap-[8px] tabletH:pb-[40px]'>
        <div className='flex gap-[8px] items-center relative shrink-0'>
          <Chip
            type='fill'
            state='disabled'
            hideIcon
            disableTypography
            disableDefaultColors
            bgclass='h-6 shrink-0 bg-grayscale-gray02 px-[6px] py-[3px] tabletH:h-auto tabletH:px-[10px] tabletH:py-[6px]'
            borderclass='ring-0'
            textclass={`${applyTypography('b12Strong')} text-text-secondary`}
          >
            {displayStep}
          </Chip>
          <p
            className={`${applyTypography(
              'b14Strong',
            )} tabletH:${applyTypography(
              'b18Strong',
            )} text-text-secondary shrink-0 leading-[1.4] tabletH:leading-[1.5]`}
          >
            {displayCategory}
          </p>
        </div>
        <p
          className={`${applyTypography('h6')} tabletH:${applyTypography(
            'h2',
          )} text-text-primary shrink-0 w-full whitespace-pre-wrap`}
          style={{ lineHeight: '1.2' }}
        >
          {title}
        </p>
      </div>

      {/* Location and Price */}
      <div className='order-2 tabletH:order-3 flex flex-col items-start justify-center pb-[16px] pt-0 px-0 relative w-full tabletH:pb-[40px]'>
        <div className='flex gap-[16px] items-start pb-[12px] pt-0 px-0 relative shrink-0 w-full tabletH:flex-col tabletH:gap-[8px] tabletH:pb-[16px]'>
          <div className='flex gap-[5px] items-center relative shrink-0'>
            <LocationIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
            <p
              className={`${applyTypography(
                'b14Strong',
              )} tabletH:${applyTypography(
                'b18Strong',
              )} text-text-primary shrink-0 leading-[1.4] tabletH:leading-[1.5]`}
            >
              {location}
            </p>
          </div>
          <div className='flex gap-[5px] items-center relative shrink-0'>
            <BillsIcon20 className='overflow-clip relative shrink-0 size-[20px]' />
            <p
              className={`${applyTypography(
                'b14Strong',
              )} tabletH:${applyTypography(
                'b18Strong',
              )} text-text-primary shrink-0 leading-[1.4] tabletH:leading-[1.5]`}
            >
              {displayPrice}
            </p>
          </div>
          {paymentNote && (
            <p
              className={`${applyTypography(
                'b13',
              )} text-text-secondary shrink-0 leading-[1.4]`}
            >
              {paymentNote}
            </p>
          )}
        </div>
        <div className='relative flex items-center w-full'>
          <p
            className={`flex-1 ${applyTypography(
              'b12',
            )} tabletH:${applyTypography(
              'b14',
            )} text-text-placeholder shrink-0 whitespace-pre-wrap`}
            style={{ lineHeight: '1.4' }}
          >
            {description}
          </p>
        </div>
      </div>

      {middleContent && <div className='order-3 w-full'>{middleContent}</div>}

      {/* Date Selection */}
      <div className='order-4 tabletH:order-2 flex flex-col items-start pb-[16px] pt-0 px-0 relative shrink-0 w-full tabletH:pb-[40px]'>
        <div className='flex items-start pb-[12px] pt-0 px-0 relative shrink-0 w-full'>
          <p
            className={`flex-1 min-h-px min-w-px ${applyTypography(
              'b18Strong',
            )} text-text-primary shrink-0 whitespace-pre-wrap`}
            style={{ lineHeight: '1.5' }}
          >
            Choose your workshop date
          </p>
        </div>
        <div className='grid grid-cols-2 gap-[8px] items-start relative shrink-0 w-full tabletH:flex tabletH:flex-wrap tabletH:gap-[24px]'>
          {displayDateSlots.length > 0 ? (
            displayDateSlots.map((slot, index) => (
              <DateSlotCard
                key={slot.id ?? index}
                slot={slot}
                isSelected={!isBlocked && selectedDateSlot === slot.id}
                onClick={handleSlotClick}
                variant='workshop'
                disabled={isBlocked}
              />
            ))
          ) : (
            <p
              className={`${applyTypography('b14')} text-text-secondary`}
              style={{ lineHeight: '1.4' }}
            >
              No available dates
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkshopInfo;
